import { PRODUCTS } from '../../data/productCatalog';

// Deterministic product recommender. Given the analysis findings + intake, it
// returns the 1–4 most directly relevant INDIVIDUAL products. It's a pure
// function with fixed tie-breaks, so the same findings + intake always yield the
// same products (repeatable across runs).
const BAND_WEIGHT = { high: 3, moderate: 2, low: 1 };
const MAX_TOTAL = 4;
const MAX_SKINCARE = 3;
const MAX_TANNING = 2;

// Stable sort: higher score first, then higher priority, then id (alphabetical).
function byRelevance(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  if (b.product.priority !== a.product.priority) return b.product.priority - a.product.priority;
  return a.product.id.localeCompare(b.product.id);
}

function severityByConcern(findings) {
  const map = {};
  (findings || []).forEach((f) => {
    const w = BAND_WEIGHT[f.band] || 1;
    if (!map[f.concernId] || w > map[f.concernId]) map[f.concernId] = w;
  });
  return map;
}

export function recommend(findings, intake) {
  const sev = severityByConcern(findings);
  const tanGoal = intake && intake.tanGoal;
  const wantsTan = tanGoal && tanGoal !== 'none';

  // Skincare: score by how strongly the product's concerns match the findings.
  const skincare = PRODUCTS
    .filter((p) => p.kind === 'skincare')
    .map((product) => {
      let score = 0;
      (product.concerns || []).forEach((c) => {
        if (sev[c]) score += sev[c];
      });
      return { product, score };
    })
    .filter((x) => x.score > 0)
    .sort(byRelevance);

  // Tanning: only when requested, matched to the desired depth.
  const tanning = wantsTan
    ? PRODUCTS
      .filter((p) => p.kind === 'tanning' && (p.depth || []).includes(tanGoal))
      .map((product) => ({ product, score: product.priority }))
      .sort(byRelevance)
    : [];

  // Reserve a slot for tanning when requested, so a tan pick isn't crowded out.
  const tanCount = Math.min(MAX_TANNING, tanning.length, wantsTan ? MAX_TOTAL - 1 : 0);
  const skinRoom = Math.min(MAX_SKINCARE, MAX_TOTAL - (tanCount > 0 ? 1 : 0));

  const picks = [
    ...skincare.slice(0, skinRoom).map((x) => x.product),
  ];
  picks.push(...tanning.slice(0, Math.min(tanCount, MAX_TOTAL - picks.length)).map((x) => x.product));
  return picks.slice(0, MAX_TOTAL);
}

// Name of the top product addressing a concern — used to fill the {product}
// token in a finding's rationale.
export function productNameForConcern(concernId) {
  const match = PRODUCTS
    .filter((p) => p.kind === 'skincare' && (p.concerns || []).includes(concernId))
    .sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id))[0];
  return match ? match.name : 'the recommended products';
}
